import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useOrg } from "./org";
import { Select } from "./people/bits";
import { ImportPreview, ImportSummary } from "./PcoPreview";
import { ImportHistory } from "./PcoHistory";

const RETURN = {
  connected: ["success", "Planning Center is connected. Choose who to import below."],
  syncing: ["success", "Planning Center is reconnected. Syncing your people now."],
  cancelled: ["error", "Planning Center wasn't connected. You can try again anytime."],
  expired: ["error", "That connection attempt timed out. Please try connecting again."],
  failed: ["error", "We couldn't finish connecting to Planning Center. Please try again."],
  in_use: ["error", "That Planning Center account is already connected to another church in Care."],
  different_account: ["error", "This church already syncs with a different Planning Center account. Disconnect it first only if you're sure."],
};

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const SyncResult = ({ s }) => {
  if (!s) return <p className="t-meta" data-testid="pco-sync-never">No sync yet.</p>;
  if (s.state === "running") return <p className="t-meta" data-testid="pco-sync-running">Syncing people from Planning Center…</p>;
  if (s.state === "failed") return <p className="status status-attention" data-testid="pco-sync-failed">{s.error} If this keeps happening, use Reconnect below.</p>;
  const p = s.result.people;
  return (
    <div data-testid="pco-sync-result">
      <p className="t-body">Last synced {when(s.finished_at)}</p>
      <p className="t-meta mt-1">{p.created} added · {p.updated} updated · {p.matched} matched to people already in Care · {p.households} households</p>
      {p.review > 0 && <p className="t-meta">{p.review} possible {p.review === 1 ? "match needs" : "matches need"} review</p>}
      {p.no_longer_included > 0 && <p className="t-meta">{p.no_longer_included} no longer on your selected Lists, moved out of routine monitoring</p>}
      {p.lists_failed?.length > 0 && <p className="status status-attention">Couldn't read: {p.lists_failed.join(", ")}. Nobody was moved out of monitoring.</p>}
      {(p.skipped_inactive > 0 || p.now_inactive > 0) && <p className="t-meta">{p.skipped_inactive} inactive people not imported{p.now_inactive ? ` · ${p.now_inactive} marked inactive` : ""}</p>}
      {s.result.groups.map((g) => <p key={g.name} className="t-meta">{g.name}: {g.error || `${g.members} people`}</p>)}
    </div>
  );
};

const contact = (x) => [x.email, x.phone, x.household].filter(Boolean).join(" · ") || "No contact details";

const MatchRow = ({ m, others, onDone }) => {
  const { api } = useOrg();
  const go = (body, msg) => api(`/integrations/pco/matches/${m.id}/resolve`, { method: "POST", body }).then(() => { toast.success(msg); onDone(); }).catch((e) => toast.error(e.message));
  const name = `${m.pco.first_name} ${m.pco.last_name}`;
  return (
    <div className="py-5" data-testid={`pco-match-${m.id}`}>
      <p className="t-name-sm">{name}</p>
      <p className="t-meta">In Planning Center: {contact(m.pco)}</p>
      <div className="mt-3 space-y-2">
        {m.candidates.map((c) => (
          <div key={c.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pl-4 border-l-2 border-line">
            <p className="t-body min-w-0">{c.preferred_name || c.first_name} {c.last_name} <span className="t-meta">in Care · {contact(c)}</span></p>
            <button onClick={() => go({ action: "confirm", person_id: c.id }, `${name} linked`)} className="btn btn-outline btn-sm shrink-0" data-testid={`pco-match-confirm-${m.id}-${c.id}`}>Same person</button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Select value="" onChange={(v) => v && go({ action: "choose", person_id: v }, `${name} linked`)} testid={`pco-match-choose-${m.id}`} className="!w-auto !h-9 text-sm">
          <option value="">Choose a different person…</option>
          {others.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
        </Select>
        <button onClick={() => go({ action: "create" }, `${name} added as a new person`)} className="btn btn-quiet btn-sm" data-testid={`pco-match-create-${m.id}`}>Add as a new person</button>
      </div>
    </div>
  );
};

const Matches = ({ onChange }) => {
  const { api } = useOrg();
  const [list, setList] = useState(null);
  const [others, setOthers] = useState([]);
  const load = () => {
    api("/integrations/pco/matches").then(setList).catch((e) => toast.error(e.message));
    api("/people").then((ps) => setOthers(ps.filter((p) => !p.external_id))).catch(() => {});
  };
  useEffect(() => { load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!list?.length) return null;
  return (
    <div className="mt-8" data-testid="pco-matches">
      <h3 className="t-label">Review matches</h3>
      <p className="t-meta mt-1">These Planning Center people might already be in Care. Linking keeps everything Care knows about them.</p>
      <div className="rule-list border-y border-line mt-4">{list.map((m) => <MatchRow key={m.id} m={m} others={others} onDone={() => { load(); onChange(); }} />)}</div>
    </div>
  );
};

const ListRow = ({ l, groups, onDone }) => {
  const { api } = useOrg();
  const [into, setInto] = useState("");
  const link = (body) => api(`/integrations/pco/lists/${l.id}/link`, { method: "POST", body })
    .then((r) => { toast.success(`${r.group.name} now follows this list`); onDone(); }).catch((e) => toast.error(e.message));
  return (
    <div className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`pco-list-${l.id}`}>
      <div className="min-w-0"><p className="t-name-sm truncate">{l.name}</p><p className="t-meta">{l.total_people ?? "—"} people{l.refreshed_at ? ` · list refreshed ${when(l.refreshed_at)}` : ""}</p></div>
      {l.group ? <p className="t-meta shrink-0" data-testid={`pco-list-linked-${l.id}`}>Care Group: <b className="text-ink">{l.group.name}</b></p> : (
        <div className="flex flex-wrap gap-2 shrink-0">
          <button onClick={() => link({})} className="btn btn-outline btn-sm" data-testid={`pco-list-create-${l.id}`}>Create Care Group</button>
          <Select value={into} onChange={(v) => { setInto(v || ""); if (v && window.confirm("Membership of this group will follow the Planning Center list. People added in Care who aren't on the list will leave the group.")) link({ group_id: v }); }} testid={`pco-list-connect-${l.id}`} className="!w-auto !h-9 text-sm">
            <option value="">Connect existing group…</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </Select>
        </div>
      )}
    </div>
  );
};

const Lists = () => {
  const { api } = useOrg();
  const [lists, setLists] = useState(null);
  const [groups, setGroups] = useState([]);
  const load = () => {
    api("/integrations/pco/lists").then(setLists).catch((e) => { setLists([]); toast.error(e.message); });
    api("/groups").then((gs) => setGroups(gs.filter((g) => g.source === "care"))).catch(() => {});
  };
  useEffect(() => { load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="mt-8" data-testid="pco-lists">
      <h3 className="t-label">Planning Center Lists</h3>
      <p className="t-meta mt-1">A Care Group linked to a list gets its members from Planning Center. Leaders and Care Rhythm stay in Care.</p>
      {!lists ? <p className="t-meta mt-4">Loading lists…</p> : !lists.length ? <p className="t-meta mt-4" data-testid="pco-lists-empty">No lists found in Planning Center.</p> : (
        <div className="rule-list border-y border-line mt-4">{lists.map((l) => <ListRow key={l.id} l={l} groups={groups} onDone={load} />)}</div>
      )}
    </div>
  );
};

const ImportSetup = ({ s, onSaved, onCancel }) => {
  const { api } = useOrg();
  const [mode, setMode] = useState(s.import_mode || "lists");
  const [lists, setLists] = useState(null);
  const [picked, setPicked] = useState(new Set((s.sync_sources || []).map((x) => x.id)));
  const [busy, setBusy] = useState(false);
  const [pv, setPv] = useState(null);
  const body = () => ({ mode, list_ids: mode === "lists" ? [...picked] : [] });
  const preview = () => {
    setBusy(true);
    api("/integrations/pco/import/preview", { method: "POST", body: body() }).then(setPv).catch((e) => toast.error(e.message)).finally(() => setBusy(false));
  };
  useEffect(() => { api("/integrations/pco/lists").then(setLists).catch((e) => { setLists([]); toast.error(e.message); }); }, [api]);
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const save = () => {
    setBusy(true);
    api("/integrations/pco/import", { method: "PUT", body: body() })
      .then(() => api("/integrations/pco/sync", { method: "POST" })).then(() => { toast.success("Importing people from Planning Center"); onSaved(); })
      .catch((e) => toast.error(e.message)).finally(() => setBusy(false));
  };
  const Option = ({ v, title, note }) => (
    <label className={`flex gap-3 p-4 border rounded-lg cursor-pointer ${mode === v ? "border-moss bg-sunk" : "border-line"}`} data-testid={`pco-import-${v}`}>
      <input type="radio" checked={mode === v} onChange={() => setMode(v)} className="mt-1 accent-[var(--moss)]" />
      <span><span className="t-name-sm block">{title}</span><span className="t-meta">{note}</span></span>
    </label>
  );
  if (pv) return <ImportPreview pv={pv} busy={busy} onConfirm={save} onBack={() => setPv(null)} />;
  return (
    <div className="mt-6" data-testid="pco-import-setup">
      <h3 className="t-label">Import people</h3>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <Option v="lists" title="Import from selected Lists (recommended)" note="Only people on the Planning Center Lists you choose come into Care." />
        <Option v="all" title="Import all active people" note="Everyone active in Planning Center comes into Care." />
      </div>
      {mode === "lists" && (
        <div className="mt-4">
          {!lists ? <p className="t-meta">Loading lists…</p> : !lists.length ? <p className="t-meta" data-testid="pco-import-no-lists">No lists found in Planning Center.</p> : (
            <div className="rule-list border-y border-line">{lists.map((l) => (
              <label key={l.id} className="py-3 flex items-center gap-3 cursor-pointer" data-testid={`pco-import-list-${l.id}`}>
                <input type="checkbox" checked={picked.has(l.id)} onChange={() => toggle(l.id)} className="accent-[var(--moss)]" />
                <span className="t-body">{l.name}</span><span className="t-meta">{l.total_people ?? "—"} people</span>
              </label>
            ))}</div>
          )}
        </div>
      )}
      <div className="flex gap-2 mt-5">
        <button disabled={busy || (mode === "lists" && !picked.size)} onClick={preview} className="btn btn-primary btn-sm" data-testid="pco-import-preview-btn">{busy ? "Checking Planning Center…" : "Preview import"}</button>
        {onCancel && <button onClick={onCancel} className="btn btn-quiet btn-sm" data-testid="pco-import-cancel">Cancel</button>}
      </div>
    </div>
  );
};

const Sources = ({ s }) => (
  <p className="t-meta mt-1" data-testid="pco-sources">{s.import_mode === "all" ? "Importing all active people" : `Importing people from: ${(s.sync_sources || []).map((x) => x.name).join(", ")}`}</p>
);

export const PlanningCenter = () => {
  const { api } = useOrg();
  const [s, setS] = useState(null);
  const [showLists, setShowLists] = useState(false);
  const [editImport, setEditImport] = useState(false);
  const [justImported, setJustImported] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const toMatches = () => document.querySelector('[data-testid="pco-matches"]')?.scrollIntoView({ behavior: "smooth" });
  const [params, setParams] = useSearchParams();
  const load = () => api("/integrations/pco").then(setS).catch((e) => toast.error(e.message));
  useEffect(() => {
    const r = RETURN[params.get("pco")];
    if (r) { toast[r[0]](r[1]); setParams({}, { replace: true }); }
    load();
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  const running = s?.last_sync?.state === "running";
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return null;
  const connect = () => api("/integrations/pco/connect", { method: "POST" }).then((r) => { window.location.href = r.url; }).catch((e) => toast.error(e.message));
  const sync = () => api("/integrations/pco/sync", { method: "POST" }).then(load).catch((e) => toast.error(e.message));
  const disconnect = () => window.confirm("Disconnect Planning Center? People and groups already in Care stay, but they'll stop updating.") &&
    api("/integrations/pco", { method: "DELETE" }).then(() => { toast("Planning Center disconnected"); setShowLists(false); load(); }).catch((e) => toast.error(e.message));
  const on = s.status === "active";
  return (
    <section className="mt-16 pt-10 border-t border-line" data-testid="pco-section">
      <h2 className="t-heading">Planning Center</h2>
      <p className="t-lead mt-2">Bring people, households and lists in from Planning Center People. KNOWN only reads from Planning Center and never changes it.</p>
      {!s.configured ? <p className="t-meta mt-5" data-testid="pco-not-configured">Planning Center isn't set up for Care yet.</p> : (
        <div className="mt-6">
          {s.status === "not_connected" && <button onClick={connect} className="btn btn-primary" data-testid="pco-connect-btn">Connect Planning Center</button>}
          {s.status === "needs_reconnect" && (
            <div data-testid="pco-needs-reconnect"><p className="status status-attention">The connection to {s.pco_org_name} has expired.</p>
              <div className="flex gap-2 mt-4"><button onClick={connect} className="btn btn-primary" data-testid="pco-reconnect-btn">Reconnect</button><button onClick={disconnect} className="btn btn-quiet" data-testid="pco-disconnect-btn">Disconnect</button></div></div>
          )}
          {on && (
            <div data-testid="pco-connected">
              <p className="status status-active" data-testid="pco-status">Connected to {s.pco_org_name}</p>
              <p className="t-meta mt-1">Connected {when(s.connected_at)}{s.connected_by_name ? ` by ${s.connected_by_name}` : ""}</p>
              {!s.import_mode || editImport ? <>
              <ImportSetup s={s} onSaved={() => { setEditImport(false); setJustImported(true); load(); }} onCancel={s.import_mode ? () => setEditImport(false) : null} />
              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={connect} className="btn btn-quiet btn-sm" data-testid="pco-setup-reconnect-btn">Reconnect</button>
                <button onClick={disconnect} className="btn btn-quiet btn-sm" data-testid="pco-setup-disconnect-btn">Disconnect</button>
              </div>
              </> : <>
              <Sources s={s} />
              {justImported && s.last_sync?.state === "done" ? <ImportSummary s={s} onReview={toMatches} /> : <div className="mt-5"><SyncResult s={s.last_sync} /></div>}
              <div className="flex flex-wrap gap-2 mt-5">
                <button disabled={running} onClick={sync} className="btn btn-primary btn-sm" data-testid="pco-sync-btn">{running ? "Syncing…" : "Sync people now"}</button>
                <button onClick={() => setEditImport(true)} className="btn btn-outline btn-sm" data-testid="pco-import-edit">Change who's imported</button>
                <button onClick={() => setShowLists(!showLists)} className="btn btn-outline btn-sm" data-testid="pco-lists-btn">{showLists ? "Hide Care Group lists" : "Lists as Care Groups"}</button>
                <button onClick={() => setShowHistory(!showHistory)} className="btn btn-outline btn-sm" data-testid="pco-history-btn">{showHistory ? "Hide import history" : "Import history"}</button>
                <button onClick={connect} className="btn btn-quiet btn-sm" data-testid="pco-reconnect-btn">Reconnect</button>
                <button onClick={disconnect} className="btn btn-quiet btn-sm" data-testid="pco-disconnect-btn">Disconnect</button>
              </div>
              {s.review_count > 0 && <Matches onChange={load} />}
              {showLists && <Lists />}
              {showHistory && <ImportHistory />}
              </>}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
